// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLanguages } from "@insite/client-framework/Store/Context/ContextSelectors";
import setLanguage from "@insite/client-framework/Store/Context/Handlers/SetLanguage";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateAccountSettings from "@insite/client-framework/Store/Pages/AccountSettings/Handlers/UpdateAccountSettings";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { LoadingSpinner } from "@insite/mobius/index";
import { LinkPresentationProps } from "@insite/mobius/Link/Link";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Select from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import xUpdateAccountSettings from "@stoneage/App/Handlers/Pages/AccountSettings/UpdateAccountSettings";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import { xScreen } from "@stoneage/XDL/Utilities";
import { xValidation } from "@stoneage/XDL/Utilities/validation";
import React, { ChangeEvent, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    updateAccountSettings,
    xUpdateAccountSettings,
    setLanguage,
};

const mapStateToProps = (state: CustomAppState) => ({
    editingAccount: state.pages.accountSettings.editingAccount,
    emailErrorMessage: state.pages.accountSettings.emailErrorMessage,
    useEmailAsUserName: getSettingsCollection(state).accountSettings.useEmailAsUserName,
    changePasswordUrl: getPageLinkByPageType(state, "ChangePasswordPage")?.url,
    firstName: state.xAccountSettings.firstName,
    lastName: state.xAccountSettings.lastName,
    phone: state.xAccountSettings.properties.phone,
    phoneError: state.xAccountSettings.phoneError,
    languages: getLanguages(state),
    language: state.xAccountSettings.properties.language,
    currentLanguage: state.context.session.language,
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AccountSettingsUserInformationStyles {
    userInformationTitle: TypographyProps;
    userInformationGridItem?: GridItemProps;
    changePasswordLink?: LinkPresentationProps;
    gridContainerStyles?: GridContainerProps;
    // Xng-Changes:
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
}

export const userInformationStyles: AccountSettingsUserInformationStyles = {
    gridContainerStyles: {
        css: css`
            ${xScreen("lt", "sm")} {
                margin: 0;
            }
        `,
    },
    userInformationGridItem: {
        width: 12,
        css: css`
            max-width: 300px;
            ${xScreen("lt", "sm")} {
                max-width: initial;
            }
        `,
    },
    changePasswordLink: {
        css: css`
            margin-left: 10px;
        `,
    },
    userInformationTitle: {
        variant: "h4",
    },
};

const styles = userInformationStyles;

const AccountSettingsUserInformation = ({
    editingAccount,
    useEmailAsUserName,
    xUpdateAccountSettings,
    firstName,
    lastName,
    phone,
    setLanguage,
    languages,
    language,
    currentLanguage,
}: Props) => {
    const phoneRequiredFieldMessage = siteMessage("ContactUsForm_PhoneNumberIsRequiredErrorMessage");
    const [currentPhoneError, setCurrentPhoneError] = useState("");
    const phoneFieldMessage = siteMessage("ContactUsForm_PhoneNumberIsInvalidErrorMessage");
    const menuId = "languageMenu";

    if (!editingAccount) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    const firstNameChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        xUpdateAccountSettings({ firstName: event.currentTarget.value } as any);
    };

    const lastNameChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        xUpdateAccountSettings({ lastName: event.currentTarget.value } as any);
    };

    const changeLanguage = (event: any) => {
        xUpdateAccountSettings({ properties: { language: event.currentTarget.value } } as any);
    };

    const phoneChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const errorMessage = !event.currentTarget.value
            ? phoneRequiredFieldMessage
            : xValidation.phone(event.currentTarget.value)
                ? ""
                : phoneFieldMessage;
        xUpdateAccountSettings({
            phoneError: errorMessage as string,
            properties: { phone: event.currentTarget.value } as any,
        });
        setCurrentPhoneError(errorMessage as string);
    };

    return (
        <>
            <Typography {...styles.userInformationTitle}>{translate("Your Information")}</Typography>
            <GridContainer {...styles.gridContainerStyles}>
                {!useEmailAsUserName && (
                    <GridItem {...styles.userInformationGridItem}>
                        <TextField
                            label={translate("User Name")}
                            value={editingAccount.userName}
                            data-test-selector="accountSettings_userName"
                            disabled
                        />
                    </GridItem>
                )}
                <GridItem {...styles.userInformationGridItem}>
                    <TextField
                        type="email"
                        label={translate("Email")}
                        value={editingAccount.email}
                        data-test-selector="accountSettings_email"
                        maxLength={256}
                        disabled
                    />
                </GridItem>
                <GridItem {...styles.userInformationGridItem}>
                    <TextField
                        type="text"
                        label={translate("First Name")}
                        onChange={firstNameChangeHandler}
                        value={firstName}
                        data-test-selector="accountSettings_firstname"
                        required
                    />
                </GridItem>
                <GridItem {...styles.userInformationGridItem}>
                    <TextField
                        type="text"
                        label={translate("Last Name")}
                        onChange={lastNameChangeHandler}
                        value={lastName}
                        required
                        data-test-selector="accountSettings_lastname"
                    />
                </GridItem>
                <GridItem {...styles.userInformationGridItem}>
                    <TextField
                        required
                        label={translate("Phone")}
                        placeholder="Your phone*"
                        onChange={phoneChangeHandler}
                        value={phone || ""}
                        error={currentPhoneError}
                        maxLength={15}
                    />
                </GridItem>
                <GridItem {...styles.userInformationGridItem}>
                    <VisuallyHidden as="label" htmlFor={menuId} id={`${menuId}-label`}>
                        {translate("Language")}
                    </VisuallyHidden>
                    <Select
                        uid={menuId}
                        data-test-selector="languageSelector"
                        onChange={changeLanguage}
                        value={language || currentLanguage?.id}
                        hasLabel
                        label="Langauge"
                    >
                        {languages?.map(c => (
                            <option value={c.id} key={c.id}>
                                {c.description}
                            </option>
                        ))}
                    </Select>
                </GridItem>
            </GridContainer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AccountSettingsUserInformation),
    definition: {
        allowedContexts: ["AccountSettingsPage"],
        group: "Account Settings",
        displayName: "User Information",
    },
};

export default widgetModule;
